import { useCallback, useMemo, useState } from 'react'
import dynamic from 'next/dynamic'
import { InstantSearch, InstantSearchSSRProvider } from 'react-instantsearch'

import { ServerState, TmCatalogProps } from 'pages/catalog/[[...slug]]'
import { environment } from '@config/environment'
import { NoResultsBoundary, VirtualConfigure, VirtualSearchbox, VirtualSortBy } from '@utils/algolia-widgets'
import { algoliaClient, flooringRefinements } from '@services/algolia'

import { VirtualInfiniteHits } from './algolia-widgets'
import {
  AtButtonState,
  AtButtonVariant,
  MlSampleCardProps,
  OrDrawer,
  OrModal,
} from '@curran-catalog/curran-atomic-library'
import { Hit } from 'instantsearch.js'
import { HandleClickProps, normalizeSampleCard, TypeSample } from '@components/rug-builder/order-samples'
import { useCart, useModal } from '@hooks/index'
import { ContentfulObjFlooringWeaveColor } from 'types'
import { SampleCartDTO } from '@services/cart'
import { useRouter } from 'next/router'
import { getErrorModalProps } from '@config/modal-error'
import { VirtualRefinementList } from '@components/menu-filters/refinement-list'
import { handleUnknownError } from '@utils/errors'
import { sendCartAnalyticsEvent } from '@utils/analytics/events/cart/send-event'
import { CartEventTypes } from '../../types/analytics/analytics-data'

const InfiniteHits = dynamic(() =>
  import(/* webpackChunkName: 'infinite-hits' */ '@templates/tm-catalog/algolia-widgets/infinite-hits').then(
    (mod) => mod.InfiniteHits,
  ),
)

export interface FlooringQueryParameters {
  cleanability?: string[]
  materialFamily?: string[]
  materialType?: string[]
  patterns?: string[]
  texture?: string[]
  style?: string[]
  types?: string[]
  usages?: string[]
  earthGentle?: string[]
}

const refinements = flooringRefinements

const TmSynsisal = ({
  ctaCard,
  loadMoreButton,
  noResultsLabel,
  noResultsContent,
  noResultsButtons,
  noResultsHelperText,
  serverState,
  serverFilterFacets,
  synsisalWeaveColorGroupCollection,
}: Pick<
  TmCatalogProps,
  'ctaCard' | 'loadMoreButton' | 'noResultsLabel' | 'noResultsContent' | 'noResultsButtons' | 'noResultsHelperText'
> &
  ServerState) => {
  const router = useRouter()
  const [isCartPageLoading, setCartPageLoading] = useState<boolean>(false)
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false)
  const [activeProduct, setActiveProduct] = useState<Hit | null>(null)
  const { isModalOpen, setIsModalOpen, modalInfo, isModalErrorOpen, setIsModalErrorOpen } = useModal()
  const { cart, addSampleToCart, removeItem } = useCart()

  const errorModalProps = getErrorModalProps(
    () => {
      setIsModalErrorOpen(false)
      router.push('/customer-service')
    },
    () => {
      setIsModalErrorOpen(false)
    },
  )

  const handleActiveProductSamples = (activeProduct: Hit) => {
    if (activeProduct) {
      setActiveProduct(activeProduct)
      setIsDrawerOpen(true)
    }
  }

  const serverResults = useMemo(
    () => serverState?.initialResults[environment.algolia.recordsIndex].results[0],
    [serverState?.initialResults],
  )

  const sampleCartItem = useCallback(
    (sampleInfo: ContentfulObjFlooringWeaveColor) => {
      return cart?.items.find(
        (itemCart) => itemCart.metadata && itemCart.metadata.contentful_id === sampleInfo.CONTENTFUL_ID,
      )
    },
    [cart],
  )

  const synsisalWeaveColorGroup = useMemo(() => {
    const activeProductId = activeProduct?.objectID
    if (activeProductId && synsisalWeaveColorGroupCollection) {
      return synsisalWeaveColorGroupCollection.find((product) => product.CONTENTFUL_ID === activeProductId)
    } else return null
  }, [activeProduct, synsisalWeaveColorGroupCollection])

  const handleToggleSample = useCallback(
    async ({
      index,
      id,
      name,
      type,
      addToCart,
      img,
      weaveId,
      borderId,
      cartId,
      borderName,
      borderColorSku,
    }: HandleClickProps) => {
      const currentItemQuantity = cart?.items?.find((item) => item.metadata?.contentful_id === id)?.quantity || 1
      const sampleInfo: SampleCartDTO = {
        color: name,
        type,
        quantity: currentItemQuantity,
        weaveReference: weaveId,
        ...(type === TypeSample.BORDER && { borderReference: borderId, borderName, borderColorSku }),
        imageAlt: img.alt,
        imageSrc: img.src,
        metadata: { contentful_id: id, type: 'sample', subType: type, link: window.location.href },
      }
      if (addToCart) {
        try {
          await addSampleToCart(sampleInfo)
          sendCartAnalyticsEvent(
            sampleInfo,
            CartEventTypes.ADD_TO_CART,
            index,
            sampleInfo.quantity,
            '',
            activeProduct?.name,
          )
        } catch (err) {
          setIsModalErrorOpen(true)
          handleUnknownError()(err)
        }
      } else {
        try {
          if (cartId) {
            await removeItem({ id: cartId })
            sendCartAnalyticsEvent(
              sampleInfo,
              CartEventTypes.REMOVE_FROM_CART,
              index,
              sampleInfo.quantity,
              '',
              activeProduct?.name,
            )
          }
        } catch (err) {
          setIsModalErrorOpen(true)
          handleUnknownError()(err)
        }
      }
    },
    [activeProduct?.name, addSampleToCart, cart?.items, removeItem, setIsModalErrorOpen],
  )

  const weaveColorSamples = useMemo(() => {
    let samples: MlSampleCardProps[] = []
    if (synsisalWeaveColorGroup?.colorGroup && synsisalWeaveColorGroup.colorGroup.weaveColors) {
      samples = synsisalWeaveColorGroup.colorGroup.weaveColors
        .filter((color) => color.status === 'Active')
        .map((color, index) =>
          normalizeSampleCard({
            index,
            item: color,
            itemName: color.weaveColorName,
            type: TypeSample.WEAVE,
            handleClick: handleToggleSample,
            cartSample: sampleCartItem(color),
            weaveId: synsisalWeaveColorGroup.CONTENTFUL_ID,
            subheader: color.samplesSubheaderSynsisal || '',
            samplesInStock: color.samplesInStock,
          }),
        )
    }
    return [
      {
        titleContainer:
          synsisalWeaveColorGroup && synsisalWeaveColorGroup.colorSamplesTitle
            ? `${synsisalWeaveColorGroup.colorSamplesTitle}`
            : `Rug Weave Samples - FREE`,
        samplesGroup: [
          {
            samples: samples,
          },
        ],
      },
    ]
  }, [synsisalWeaveColorGroup, sampleCartItem, handleToggleSample])

  return (
    <>
      {synsisalWeaveColorGroup?.hasSamples && (
        <OrDrawer
          isOpen={isDrawerOpen}
          title="Order Samples"
          description={synsisalWeaveColorGroup.samplesDrawerDescription}
          sampleGroups={weaveColorSamples}
          actionButton={{
            label: 'View Cart',
            onClick: () => {
              setCartPageLoading(true)
              router.push('/cart')
            },
            state: isCartPageLoading ? AtButtonState.LOADING : AtButtonState.ENABLED,
            icon: isCartPageLoading ? { type: 'spinner' } : undefined,
            disabled: isCartPageLoading,
          }}
          handleCloseDrawer={() => {
            setIsDrawerOpen(false)
          }}
        />
      )}
      <InstantSearchSSRProvider {...serverState}>
        <InstantSearch
          searchClient={algoliaClient}
          indexName={environment.algolia.recordsIndex}
          insights={true}
          future={{ preserveSharedStateOnUnmount: true }}
        >
          {/* A virtual widgets but which doesn't render anything */}
          {/* https://www.algolia.com/doc/guides/building-search-ui/widgets/customize-an-existing-widget/react-hooks/#building-a-virtual-widget-with-hooks */}
          <VirtualConfigure serverFilterFacets={serverFilterFacets} serverQuery={serverResults?.query} />
          <VirtualSearchbox />
          <VirtualSortBy />
          {refinements.map(({ options }, index) => (
            <VirtualRefinementList
              key={`${options?.attribute}-${index}`}
              attribute={options?.attribute as string}
              limit={options?.limit as number}
              showMore={options?.showMore as boolean}
              showMoreLimit={options?.showMoreLimit as number}
            />
          ))}
          <VirtualInfiniteHits />
          <NoResultsBoundary
            nbHits={serverResults?.nbHits}
            noResultsLabel={noResultsLabel}
            noResultsContent={noResultsContent}
            noResultsButtons={noResultsButtons}
            noResultsHelperText={noResultsHelperText}
          >
            <div className="flex flex-row gap-5">
              <InfiniteHits
                cart={cart}
                ctaCard={ctaCard}
                loadMoreButton={loadMoreButton}
                showRefinements={false}
                setActiveProduct={handleActiveProductSamples}
                synsisalWeaveColorGroupCollection={synsisalWeaveColorGroupCollection}
              />
            </div>
          </NoResultsBoundary>
        </InstantSearch>
      </InstantSearchSSRProvider>
      {/*Modal order*/}
      <OrModal
        productContent={modalInfo?.productContent}
        isOpen={isModalOpen}
        icon="circle-check"
        title="Added to Shopping Cart"
        leftButton={{
          label: 'Continue Shopping',
          onClick: () => {
            setIsModalOpen(false)
          },
          variant: AtButtonVariant.TERTIARY,
        }}
        rightButton={{
          label: 'View Cart',
          onClick: () => {
            setCartPageLoading(true)
            router.push('/cart')
          },
          state: isCartPageLoading ? AtButtonState.LOADING : AtButtonState.ENABLED,
          icon: isCartPageLoading ? { type: 'spinner' } : undefined,
          disabled: isCartPageLoading,
        }}
        handleCloseModal={() => setIsModalOpen(false)}
      />

      <OrModal
        isOpen={isModalErrorOpen}
        title="Error"
        description={errorModalProps.description}
        leftButton={errorModalProps.leftButton}
        rightButton={errorModalProps.rightButton}
        handleCloseModal={() => setIsModalErrorOpen(false)}
      />
    </>
  )
}

export default TmSynsisal
